import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BookOpen,
  Users,
  ArrowLeftRight,
  Bookmark,
  Receipt,
  BarChart3,
  Library,
  User as UserIcon,
  Key,
  LogOut,
  ChevronUp,
} from "lucide-react";
import api from "../../../connector/api";

const initialTotals = {
  total_books: 0,
  total_patrons: 0,
  borrowed_books: 0,
  overdue_books: 0,
  total_fines: 0,
};

export function Sidebar({
  totals: totalsProp, // Optional override from parent
  user: userProp = { name: "System Admin", email: "admin@cais.edu", initials: "CA" },
  onLogout,
  onViewProfile,
  onChangePassword,
}) {
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(null);
  const [fetchedTotals, setFetchedTotals] = useState(initialTotals);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Fallback to prop if provided, otherwise use internally fetched totals
  const totals = totalsProp || fetchedTotals;

  // Fetch Sidebar data (User Profile & Dashboard Totals)
  useEffect(() => {
    let isMounted = true;

    const fetchSidebarData = async () => {
      try {
        if (typeof api !== "undefined") {
          // Fetch User Profile
          const userRes = await api.get("accounts/me/");
          if (userRes.data?.user && isMounted) {
            setCurrentUser({
              ...userRes.data.user,
              permissions: userRes.data.permissions || [],
            });
          }

          // Fetch Dashboard Totals (only if parent didn't pass totalsProp)
          if (!totalsProp) {
            const dashboardRes = await api.get("cataloging/dashboard/");
            if (dashboardRes.data?.totals && isMounted) {
              setFetchedTotals(dashboardRes.data.totals);
            }
          }
        }
      } catch (error) {
        console.error("Could not load sidebar data:", error);
      }
    };

    fetchSidebarData();
    return () => {
      isMounted = false;
    };
  }, [totalsProp]);

  // Merge fetched profile data with props fallback
  const activeUser = {
    name: currentUser?.name || currentUser?.username || userProp.name,
    email: currentUser?.email || userProp.email,
    initials:
      currentUser?.initials ||
      (currentUser?.name
        ? currentUser.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
        : userProp.initials),
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      sessionStorage.clear();
      localStorage.clear();
      window.location.href = "/login";
    }
  };

  // Close user dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const mainNav = [
    { label: "Dashboard", icon: BookOpen, path: "/library" },
    {
      label: "Cataloging",
      icon: BookOpen,
      path: "/library/cataloging",
      badge: totals.total_books,
    },
    {
      label: "Patrons",
      icon: Users,
      path: "/library/patrons",
      badge: totals.total_patrons,
    },
  ];

  const circulationNav = [
    {
      label: "Circulation",
      icon: ArrowLeftRight,
      path: "/library/circulation",
      badge: totals.borrowed_books,
    },
    { label: "Reservations", icon: Bookmark, path: "" },
    {
      label: "Fines",
      icon: Receipt,
      path: "/library/payfine",
      badge: totals.total_fines ?? totals.overdue_books,
      badgeColor: "bg-rose-500/20 text-rose-400",
    },
  ];

  const adminNav = [
    { label: "Reports", icon: BarChart3, path: "/library/reports" },
  ];

  const renderNavGroup = (title, items) => (
    <div className="space-y-1">
      <h4 className="px-3 text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-2">
        {title}
      </h4>
      {items.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;

        return (
          <Link
            key={item.label}
            to={item.path}
            className={`flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-medium transition-all duration-200 ${
              isActive
                ? "bg-[#28231d] text-amber-400 font-semibold shadow-sm"
                : "text-stone-400 hover:text-stone-200 hover:bg-[#211d18]"
            }`}
          >
            <div className="flex items-center gap-3">
              <Icon className={`w-4 h-4 transition-colors ${isActive ? "text-amber-400" : "text-stone-400"}`} />
              <span>{item.label}</span>
            </div>
            {item.badge !== undefined && item.badge !== null && (
              <span
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full transition-colors ${
                  item.badgeColor || "bg-amber-500/10 text-amber-400"
                }`}
              >
                {item.badge}
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );

  return (
    <aside className="w-64 bg-[#181512] border-r border-[#26211b] flex flex-col justify-between h-screen sticky top-0 p-4 shrink-0 font-sans transition-all duration-300 ease-in-out">
      <div className="space-y-6">
        {/* Brand Header */}
        <div className="flex items-center gap-3 px-2 py-1">
          <div className="w-8 h-8 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400">
            <Library className="w-4 h-4" />
          </div>
          <div>
            <h1 className="text-sm font-serif font-black tracking-widest uppercase text-stone-100">
              CAIS
            </h1>
            <p className="text-[9px] font-sans tracking-widest text-stone-500 font-bold uppercase">
              Library System
            </p>
          </div>
        </div>

        {/* Navigation Groups */}
        <div className="space-y-6">
          {renderNavGroup("Main", mainNav)}
          {renderNavGroup("Circulation", circulationNav)}
          {renderNavGroup("Administration", adminNav)}
        </div>
      </div>

      {/* Profile Footer & Popover Menu */}
      <div className="pt-4 border-t border-[#26211b] relative" ref={userMenuRef}>
        <div
          className={`absolute bottom-full left-0 right-0 mb-2 bg-[#1c1814] border border-[#2c2620] rounded-2xl p-1.5 shadow-2xl space-y-0.5 transition-all duration-200 origin-bottom z-50 ${
            isUserMenuOpen
              ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
              : "opacity-0 scale-95 translate-y-2 pointer-events-none"
          }`}
        >
          <button
            type="button"
            onClick={() => {
              setIsUserMenuOpen(false);
              onViewProfile?.();
            }}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-medium text-stone-300 hover:text-amber-400 hover:bg-[#25201b] transition-colors"
          >
            <UserIcon className="w-4 h-4 text-stone-400" />
            <span>View Profile</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setIsUserMenuOpen(false);
              onChangePassword?.();
            }}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-medium text-stone-300 hover:text-amber-400 hover:bg-[#25201b] transition-colors"
          >
            <Key className="w-4 h-4 text-stone-400" />
            <span>Change Password</span>
          </button>

          <div className="h-px bg-[#26211b] my-1" />

          <button
            type="button"
            onClick={() => {
              setIsUserMenuOpen(false);
              handleLogout();
            }}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-medium text-rose-400 hover:bg-rose-500/10 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>

        {/* User Card Trigger */}
        <button
          type="button"
          onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
          className={`w-full flex items-center justify-between p-2 rounded-xl bg-[#211d18] border border-[#2c2620] hover:border-amber-500/30 transition-all duration-200 group ${
            isUserMenuOpen ? "border-amber-500/40 bg-[#26211b]" : ""
          }`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold text-xs shrink-0">
              {activeUser.initials}
            </div>
            <div className="min-w-0 text-left">
              <p className="text-xs font-bold text-stone-200 truncate group-hover:text-amber-400 transition-colors">
                {activeUser.name}
              </p>
              <p className="text-[10px] text-stone-500 truncate">{activeUser.email}</p>
            </div>
          </div>
          <ChevronUp
            className={`w-4 h-4 text-stone-500 transition-transform duration-200 shrink-0 ${
              isUserMenuOpen ? "rotate-180 text-amber-400" : "group-hover:text-stone-300"
            }`}
          />
        </button>
      </div>
    </aside>
  );
}